import { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  MenuItem,
  Tooltip,
  Typography,
  Menu,
  Divider,
  IconButton,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import LogoutIcon from "@mui/icons-material/Logout";
import EngineeringIcon from "@mui/icons-material/Engineering";

import { switchOrg, signoutUser } from "../store/actions/auth";
import type { AppDispatch } from "../store/state/rootStore";
import type { RootState } from "../store/state/rootReducer";
import { selectOrgSwitcherOrganizations } from "../store/selectors/orgSwitcher";

// Same crossfade trick as OrganizationLogo.tsx: fade the whole pill out, swap
// its layout (expanded dropdown vs. bare avatar) while invisible, fade back in
// — avoids animating width/height directly, which read as a jarring "jump".
const FADE_MS = 120;

/**
 * OrgSwitcher — visible to users whether they manage one or multiple organizations.
 */
interface OrgSwitcherProps {
  isExpanded?: boolean;
}

export const OrgSwitcher = ({ isExpanded = true }: OrgSwitcherProps) => {
  const theme = useTheme();
  // ease-out (decelerate) when settling into the expanded state, ease-in
  // (accelerate) when collapsing away — matches the drawer's own width transition.
  const sidebarEasing = isExpanded ? theme.transitions.easing.easeOut : theme.transitions.easing.easeIn;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const organizationInfo = useSelector((state: RootState) => state.auth.organization_info);
  const orgsList = useSelector(selectOrgSwitcherOrganizations);
  const userProfile = useSelector((state: RootState) => state.profile?.user);

  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [isTransitioning, setIsTransitioning] = useState(false);
  // mirrors `isExpanded`, but only updates once faded out, so the layout swap
  // (dropdown pill vs. bare avatar) happens while invisible instead of
  // visibly resizing
  const [displayedExpanded, setDisplayedExpanded] = useState(isExpanded);

  useEffect(() => {
    if (isExpanded === displayedExpanded) {
      return undefined;
    }
    setIsTransitioning(true);
    const timeoutId = setTimeout(() => {
      setDisplayedExpanded(isExpanded);
      setIsTransitioning(false);
    }, FADE_MS);

    return () => clearTimeout(timeoutId);
  }, [isExpanded, displayedExpanded]);

  const currentOrgId = (organizationInfo as { _id?: string })?._id ?? "";
  const currentOrgName = (organizationInfo as { organization_name?: string })?.organization_name ?? "";

  const activeOrg = useMemo(
    () => orgsList.find((org) => org._id === currentOrgId) || organizationInfo,
    [orgsList, currentOrgId, organizationInfo]
  );
  const activeOrgLogo = activeOrg as { backgroundUrl?: string; logoUrl?: string } | null;
  const effectiveLogo = activeOrgLogo?.backgroundUrl || activeOrgLogo?.logoUrl;
  const showLogo = !!effectiveLogo;

  if (!organizationInfo || !userProfile) return null;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = async (selectedOrgId: string) => {
    handleClose();
    if (selectedOrgId === currentOrgId || loading) return;
    setLoading(true);
    try {
      await dispatch(switchOrg(selectedOrgId));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    handleClose();
    dispatch(signoutUser());
  };

  const handleAdmin = () => {
    handleClose();
    navigate("/admin");
  };

  const normalizeMicrosoftCredentials = (creds: string): string => {
    if (!creds) return "";
    const extMatch = creds.match(/^([^#]+)#ext#@/i);
    return extMatch ? extMatch[1].replace(/_/g, "@") : creds;
  };

  const normalizedEmail = normalizeMicrosoftCredentials(userProfile?.email || "");
  const normalizedName = normalizeMicrosoftCredentials(userProfile?.full_name || "");

  const handleCopyId = () => {
    if (userProfile?.user_id) {
      navigator.clipboard.writeText(`${normalizedName} - ${userProfile.user_id}`);
    }
  };

  const Thumbnail = () => {
    const [imgLoaded, setImgLoaded] = useState(false);
    const [imgError, setImgError] = useState(false);

    const showFallback = !showLogo || imgError || !imgLoaded;

    return (
      <Box
        sx={{
          width: displayedExpanded ? 24 : 32,
          height: displayedExpanded ? 24 : 32,
          borderRadius: "6px",
          backgroundColor: "#1C7C6F",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: displayedExpanded ? "10px" : "12px",
          fontWeight: "bold",
          flexShrink: 0,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {showLogo && !imgError && (
          <img
            src={effectiveLogo}
            alt={currentOrgName}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: imgLoaded ? "block" : "none",
            }}
          />
        )}
        {showFallback && (
          <Typography sx={{ fontSize: "inherit", fontWeight: "inherit" }}>
            {currentOrgName.substring(0, 3).toUpperCase()}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        mt: 1,
      }}
    >
      <Tooltip
        title="Switch organization"
        placement="right"
        disableHoverListener={isExpanded}
        enterDelay={400}
        disableInteractive
      >
        <Box
          sx={{
            width: "100%",
            opacity: isTransitioning ? 0 : 1,
            transition: `opacity ${FADE_MS}ms ${sidebarEasing}`,
          }}
        >
          <Box
            onClick={loading ? undefined : handleClick}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: displayedExpanded ? "10px" : 0,
              px: displayedExpanded ? "12px" : "0",
              py: displayedExpanded ? "8px" : "0",
              width: "100%",
              borderRadius: "10px",
              backgroundColor: displayedExpanded ? "#ffffff" : "rgba(255, 255, 255, 0)",
              border: "1px solid",
              borderColor: displayedExpanded ? "#E2E8F0" : "rgba(226, 232, 240, 0)",
              boxShadow: displayedExpanded ? "0px 1px 2px rgba(0, 0, 0, 0.05)" : "0px 1px 2px rgba(0, 0, 0, 0)",
              cursor: loading ? "default" : "pointer",
              "&:hover": {
                backgroundColor: displayedExpanded ? "#F7FAFC" : "transparent",
                opacity: displayedExpanded ? 1 : 0.8,
              },
            }}
          >
            {loading && !displayedExpanded ? <CircularProgress size={24} sx={{ color: "#239B8B" }} /> : <Thumbnail />}

            {displayedExpanded && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexGrow: 1,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                {loading ? (
                  <CircularProgress size={16} sx={{ color: "#4A5568", ml: "auto" }} />
                ) : (
                  <>
                    <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#4A5568", flexGrow: 1 }} noWrap>
                      {currentOrgName}
                    </Typography>
                    <KeyboardArrowDownIcon sx={{ fontSize: 20, color: "#4A5568", flexShrink: 0 }} />
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            p: 1,
            minWidth: 260,
            borderRadius: "12px",
            border: "1px solid rgba(0,0,0,0.08)",
            boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.1)",
          },
        }}
        transformOrigin={{ horizontal: isExpanded ? "center" : "left", vertical: "top" }}
        anchorOrigin={{ horizontal: isExpanded ? "center" : "right", vertical: "bottom" }}
      >
        {orgsList.map((org) => {
          const isSelected = org._id === currentOrgId;
          return (
            <MenuItem
              key={org._id as string}
              onClick={() => handleChange(org._id as string)}
              sx={{
                py: 1.2,
                px: 2,
                mb: 0.5,
                borderRadius: "8px",
                display: "flex",
                justifyContent: "space-between",
                backgroundColor: isSelected ? "#E0F1EE" : "transparent",
                color: isSelected ? "#1C7C6F" : "#2D3748",
                "&:hover": {
                  backgroundColor: isSelected ? "#D1EAE5" : "#F7FAFC",
                },
              }}
            >
              <Typography fontSize="14px" fontWeight={isSelected ? 600 : 400} noWrap>
                {(org as { organization_name?: string }).organization_name}
              </Typography>
              {isSelected && <CheckIcon sx={{ fontSize: 18, color: "#1C7C6F" }} />}
            </MenuItem>
          );
        })}

        {orgsList.length > 0 && <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.06)" }} />}

        <MenuItem
          onClick={handleLogout}
          sx={{
            py: 1.2,
            px: 2,
            gap: 1.5,
            borderRadius: "8px",
            color: "#4A5568",
            "&:hover": { backgroundColor: "#F7FAFC" },
          }}
        >
          <LogoutIcon sx={{ fontSize: 20 }} />
          <Typography fontSize="14px" fontWeight={500}>
            Log out
          </Typography>
        </MenuItem>

        <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.06)" }} />

        <Box
          onClick={handleCopyId}
          sx={{
            py: 1.5,
            px: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#2D3748" }}>{normalizedName}</Typography>
            <Typography sx={{ fontSize: "14px", color: "#718096" }}>{normalizedEmail}</Typography>
          </Box>

          {userProfile?.role === "admin" && (
            <Tooltip title="Admin dashboard">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleAdmin();
                }}
                size="small"
                sx={{ color: "#239B8B" }}
              >
                <EngineeringIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Menu>
    </Box>
  );
};
